import { create } from 'zustand';

import { api } from '../lib/api.js';
import { docIO, SAVE_DELAY_MS } from '../lib/doc-io.js';
import { logEditor } from './console.js';
import { useWorkspace } from './workspace.js';

/**
 * The open node graph (`*.vgraph.json` visual script or `*.shadergraph.json`
 * shader graph). Edits replace the whole immutable document, go on the undo
 * stack (registered with the app's undo service in shell-setup), autosave
 * after a short debounce and then compile through `prism graph compile`
 * (server route /graph/compile); diagnostics name the failing nodes.
 */

export type GraphKind = 'vgraph' | 'shadergraph';

export interface GraphNode {
  id: string;
  type: string;
  pos: [number, number];
  data: Record<string, unknown>;
}
export interface GraphEdge {
  from: string; // "<node>.<pin>"
  to: string;
}
/** Editor-only decoration stored in `groups`. */
export interface GraphGroup {
  id: string;
  kind: 'group' | 'comment';
  pos: [number, number];
  size: [number, number];
  text: string;
}
export interface GraphDoc {
  format: string;
  name: string;
  nodes: GraphNode[];
  edges: GraphEdge[];
  variables: { name: string; type: string; default?: unknown }[];
  groups: GraphGroup[];
  master?: string;
}
export interface GraphDiag {
  node: string | null;
  message: string;
}

const HISTORY_LIMIT = 200;
export type GraphSaveState = 'saved' | 'pending' | 'saving' | 'error';

export function graphKindOf(path: string): GraphKind | null {
  if (/\.vgraph\.json$/i.test(path)) return 'vgraph';
  if (/\.shadergraph\.json$/i.test(path)) return 'shadergraph';
  return null;
}

export function newGraphDoc(kind: GraphKind, name: string): GraphDoc {
  const nodes: GraphNode[] =
    kind === 'vgraph'
      ? [{ id: 'n1', type: 'event_start', pos: [60, 60], data: {} }]
      : [{ id: 'master', type: 'master_pbr', pos: [600, 80], data: {} }];
  return { format: kind === 'vgraph' ? 'prism.vgraph/1' : 'prism.shadergraph/1', name, nodes, edges: [], variables: [], groups: [] };
}

export function parseGraph(text: string, kind: GraphKind): GraphDoc {
  const raw = JSON.parse(text) as Partial<GraphDoc>;
  const base = newGraphDoc(kind, raw.name ?? '');
  return {
    ...base,
    ...raw,
    nodes: (raw.nodes ?? []).map((n) => ({ ...n, pos: n.pos ?? [0, 0], data: n.data ?? {} })),
    edges: raw.edges ?? [],
    variables: raw.variables ?? [],
    groups: raw.groups ?? [],
  } as GraphDoc;
}

interface GraphState {
  path: string | null;
  kind: GraphKind | null;
  doc: GraphDoc | null;
  past: GraphDoc[];
  future: GraphDoc[];
  saveState: GraphSaveState;
  diagnostics: GraphDiag[];
  compiled: boolean | null;
  error: string | null;
  open: (path: string) => Promise<void>;
  /** Replaces the document. `history: false` for the continuation of a drag. */
  commit: (doc: GraphDoc, options?: { history?: boolean }) => void;
  undo: () => void;
  redo: () => void;
  flush: () => Promise<void>;
  close: () => void;
  /** An external change of the open file (hot reload). */
  fileChanged: (path: string) => void;
}

let saveTimer: ReturnType<typeof setTimeout> | null = null;
let lastText = '';

const projectId = (): number | null => useWorkspace.getState().projectId ?? null;
const errorText = (e: unknown): string => (e instanceof Error ? e.message : String(e));

export const useGraph = create<GraphState>((set, get) => {
  function schedule(): void {
    if (saveTimer) clearTimeout(saveTimer);
    saveTimer = setTimeout(() => void get().flush(), SAVE_DELAY_MS);
    set({ saveState: 'pending' });
  }

  async function compile(path: string, pid: number): Promise<void> {
    try {
      const r = await api.post<{ ok: boolean; diagnostics: GraphDiag[] }>(`/projects/${pid}/graph/compile`, { path });
      if (get().path === path) set({ compiled: r.ok, diagnostics: r.diagnostics });
    } catch (error) {
      if (get().path === path) set({ compiled: null, diagnostics: [{ node: null, message: errorText(error) }] });
    }
  }

  return {
    path: null,
    kind: null,
    doc: null,
    past: [],
    future: [],
    saveState: 'saved',
    diagnostics: [],
    compiled: null,
    error: null,

    open: async (path) => {
      const kind = graphKindOf(path);
      const pid = projectId();
      if (!kind || !pid) return;
      await get().flush();
      set({ path, kind, doc: null, past: [], future: [], error: null, diagnostics: [], compiled: null });
      try {
        const text = await docIO().read(pid, path);
        if (get().path !== path) return;
        lastText = text;
        set({ doc: parseGraph(text, kind), saveState: 'saved' });
        void compile(path, pid);
      } catch (error) {
        if (get().path === path) set({ error: `${path}: ${errorText(error)}` });
      }
    },

    commit: (doc, options = {}) => {
      const { history = true } = options;
      const prev = get().doc;
      set((s) => ({
        doc,
        past: history && prev ? [...s.past, prev].slice(-HISTORY_LIMIT) : s.past,
        future: history ? [] : s.future,
      }));
      schedule();
    },

    undo: () => {
      const { past, doc, future } = get();
      const prev = past[past.length - 1];
      if (!prev || !doc) return;
      set({ doc: prev, past: past.slice(0, -1), future: [doc, ...future] });
      schedule();
    },

    redo: () => {
      const { past, doc, future } = get();
      const next = future[0];
      if (!next || !doc) return;
      set({ doc: next, past: [...past, doc], future: future.slice(1) });
      schedule();
    },

    flush: async () => {
      if (saveTimer) clearTimeout(saveTimer);
      saveTimer = null;
      const { path, doc, saveState } = get();
      const pid = projectId();
      if (!path || !doc || !pid || saveState === 'saved') return;
      set({ saveState: 'saving' });
      const text = `${JSON.stringify(doc, null, 2)}\n`;
      try {
        await docIO().write(pid, path, text);
        lastText = text;
        if (get().path === path) set({ saveState: get().doc === doc ? 'saved' : 'pending' });
        await compile(path, pid);
      } catch (error) {
        set({ saveState: 'error' });
        logEditor('error', `Could not save ${path}: ${errorText(error)}`);
      }
    },

    close: () => {
      if (saveTimer) clearTimeout(saveTimer);
      saveTimer = null;
      set({ path: null, kind: null, doc: null, past: [], future: [], diagnostics: [], compiled: null, error: null, saveState: 'saved' });
    },

    fileChanged: (path) => {
      const { path: open, saveState } = get();
      if (open !== path || saveState !== 'saved') return;
      const pid = projectId();
      const kind = graphKindOf(path);
      if (!pid || !kind) return;
      void docIO()
        .read(pid, path)
        .then((text) => {
          if (text === lastText || get().path !== path) return;
          lastText = text;
          set({ doc: parseGraph(text, kind) });
        })
        .catch(() => undefined);
    },
  };
});
